import { useState, useMemo } from "react";
import { Search, ClipboardList, FolderOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useInvestigationTemplates, useInvestigationTemplateGroups, type InvestigationTemplate, type Investigation } from "@/lib/queries";
import { bucketTemplates } from "@/lib/templateGroups";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Full catalog so template item ids can be resolved to display names. */
  investigations: Investigation[];
  onPick: (template: InvestigationTemplate) => void;
}

/**
 * Picker for stored investigation templates, opened by the "Insert Template"
 * button in the EMR visit page's Investigations column (mirrors the
 * prescription card's PrescriptionTemplatePickerDialog). Picking a template
 * hands the whole template object to the parent, which merges its item ids
 * into the current selection (duplicates skipped) and opens the batch
 * details dialog for any newly-added Rad/EP tests — the same persist path as
 * the old "Add template" select dropdown it replaced.
 *
 * Since migration 0037 (2026-11-04) templates carry an optional group — the
 * list renders as per-group accordions ("Ungrouped" pinned last), mirroring
 * the prescription picker. ALL accordions start COLLAPSED (the 2026-11-02
 * prescription-picker behavior, memory #4421/post-mortem #295). The filter
 * matches the template name OR any test name ("CBC" surfaces every protocol
 * containing it) and PRESERVES the accordion structure: only groups holding
 * a match render (empty buckets are dropped by bucketTemplates), each still
 * collapsed until opened.
 */
export function InvestigationTemplatePickerDialog({ open, onOpenChange, investigations, onPick }: Props) {
  const { data, isLoading } = useInvestigationTemplates();
  const { data: groupsData } = useInvestigationTemplateGroups();
  const [query, setQuery] = useState("");
  // Accordion expansion: starts EMPTY — every accordion (named groups and
  // "Ungrouped") renders COLLAPSED until the user opens it (the prescription
  // picker behavior since 2026-11-02). Controlled state persists across
  // typing/search-clears within one dialog session; reset on dialog close.
  const [expanded, setExpanded] = useState<string[]>([]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const groups = useMemo(() => groupsData?.rows ?? [], [groupsData]);
  const invById = useMemo(() => new Map(investigations.map((i) => [i.id, i.name])), [investigations]);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.items.some((id) => (invById.get(id) ?? "").toLowerCase().includes(q)));
  }, [rows, q, invById]);

  // Group buckets over the FILTERED list (shared with the management page):
  // searching keeps the accordions — only groups holding a match render,
  // each still collapsed; the user opens one and picks a template.
  const grouped = useMemo(() => bucketTemplates(filtered, groups), [filtered, groups]);

  // Reset expansion + search when the dialog closes.
  const reset = () => { setExpanded([]); setQuery(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">Investigation templates</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input
            autoFocus
            className="pl-9"
            placeholder="Search template or test name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <ScrollArea className="h-[50vh] rounded-lg border border-cream-200 dark:border-dark-700">
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-2 p-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : grouped.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted">
                {rows.length === 0
                  ? "No templates stored yet — create them from the Investigations page (Templates tab) or the “Save as template” button."
                  : "No templates match your search."}
              </div>
            ) : (
              <Accordion
                type="multiple"
                value={expanded}
                onValueChange={setExpanded}
                className="space-y-1"
              >
                {grouped.map((b) => (
                  <AccordionItem key={b.key} value={b.key} className="border border-cream-200 rounded-lg px-3 dark:border-dark-700">
                    <AccordionTrigger className="py-2.5 text-left text-sm font-medium text-maroon-700 hover:no-underline dark:text-amber-500">
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <FolderOpen size={15} className="shrink-0 text-maroon-600 dark:text-amber-500" />
                        <span className="min-w-0 break-words text-left">{b.name}</span>
                        <span className="shrink-0 whitespace-nowrap text-xs font-normal text-muted">
                          {b.items.length} template{b.items.length === 1 ? "" : "s"}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-1.5">
                      {b.items.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className="flex w-full items-start gap-2.5 rounded-lg border border-cream-200 bg-white p-2.5 text-left transition-colors hover:bg-cream-50/50 dark:border-dark-700 dark:bg-dark-800"
                          onClick={() => { onPick(t); onOpenChange(false); }}
                        >
                          <ClipboardList size={15} className="mt-0.5 shrink-0 text-maroon-600 dark:text-amber-500" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-2">
                              <span className="min-w-0 break-words text-sm font-medium text-ink dark:text-cream-50">{t.name}</span>
                              <span className="shrink-0 whitespace-nowrap text-xs text-muted">{t.items.length} test{t.items.length === 1 ? "" : "s"}</span>
                            </span>
                            <span className="mt-0.5 block break-words text-xs text-muted">
                              {t.items.map((id) => invById.get(id) ?? "?").join(", ") || "—"}
                            </span>
                          </span>
                        </button>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </ScrollArea>
        <p className="text-xs text-muted">
          Selecting a template adds its tests to the current selection (already-selected tests are kept).
        </p>
      </DialogContent>
    </Dialog>
  );
}